import type { TImportedPage } from "@/shared/services/pages/types";
import { DocumentGridPreview } from "@pages/stories/components/documents/document-grid-preview";
import { formatLastActivity } from "@pages/stories/components/documents/last-activity";
import { useId } from "react";
import { useNavigate } from "react-router-dom";
import { useRecentImports } from "../hooks/use-recent-imports";
import { closeImportDialog } from "../utils/import-dialog-id";

export const RecentlyImportedList = () => {
  const navigate = useNavigate();
  const headingId = useId();
  const pages = useRecentImports();

  const handleOpen = (page: TImportedPage) => {
    closeImportDialog();
    navigate(`/pages/editor/${page._id}`);
  };

  if (!pages.length) return null;

  return (
    <section
      aria-labelledby={headingId}
      className="tw-mt-14 tw-flex tw-flex-col tw-items-center tw-gap-6"
    >
      <h3
        id={headingId}
        className="tw-m-0 tw-text-center tw-text-xl tw-font-semibold tw-leading-7 tw-text-blue"
      >
        Recently imported:
      </h3>
      <ul className="tw-m-0 tw-flex tw-w-[528px] tw-max-w-full tw-list-none tw-flex-col tw-p-0">
        {pages.map((page) => (
          <li
            key={page._id}
            className="tw-relative tw-flex tw-items-center tw-border-0 tw-border-b tw-border-solid tw-border-blue-50 tw-py-2 hover:tw-bg-blue-50/50 has-[:focus-visible]:tw-outline has-[:focus-visible]:tw-outline-2 has-[:focus-visible]:tw-outline-violet-700"
          >
            <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-4 tw-p-2">
              <div
                aria-hidden="true"
                className="tw-pointer-events-none tw-h-[54px] tw-w-[86px] tw-shrink-0 tw-overflow-hidden tw-rounded-md tw-bg-blue-50 tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-black/10 [&>div]:tw-h-full [&>div]:tw-rounded-none [&>div]:tw-outline-none"
              >
                <DocumentGridPreview
                  contentPointer={page.settings.contentPointer ?? undefined}
                  previewUrl={page.previewUrl}
                />
              </div>
              <button
                type="button"
                onClick={() => handleOpen(page)}
                className="tw-min-w-0 tw-truncate tw-text-left tw-text-sm tw-font-medium tw-leading-5 tw-text-blue after:tw-absolute after:tw-inset-0 after:tw-content-[''] focus-visible:tw-outline-none"
              >
                {page.settings.importedFrom.fileName}
              </button>
            </div>
            <p className="tw-m-0 tw-w-[104px] tw-shrink-0 tw-p-2 tw-text-sm tw-leading-5 tw-text-blue-500">
              {formatLastActivity(page.settings.importedFrom.importedAt)}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
};
